import styled from '@emotion/styled'
import { CircularProgress, Divider, Stack, Typography } from '@mui/material'
import { usersUpdateTwinResponder } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { Feature, OnboardingStep, TwinResponderUpdateStatus } from 'api/model'
import { COLORS } from 'app-constants'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import FloatingButtonBar from 'components/FloatingButtonBar'
import { AdvancedSettingsBannerWithOnboardingStep } from 'components/Gmail'
import { AdvancedSettings, JELLED_EMAIL_DOMAINS } from 'components/Gmail/SettingsModal'
import JelledSwitch from 'components/JelledSwitch'
import SetupPermissionsModal from 'components/Permissions'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useAdvancedSettings, useFetchTwinResponder } from 'hooks/use-email-responder'
import { useActiveOrganization } from 'hooks/use-organization'
import { useFetchCheckPermissions } from 'hooks/use-permission'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, organizationFamily, toastAtom } from 'state'
import { myTwinAtom } from 'state/my-twin'
import { featurePermissionsAtomFamily } from 'state/permission'

export const PageContainerStack = styled(Stack)`
  border-left: 1px solid #ddd;
  width: 100%;
  height: 100vh;
  padding: 24px;
  background-color: #f8f8f8;
`

const EmailSettings = ({ updateOnboardingStep }: { updateOnboardingStep: () => void }) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const user = useRecoilValue(authenticatedUserAtom)
  const [searchParams] = useSearchParams()
  const isMobile = useCheckMobileScreen()
  const organization = useRecoilValue(organizationFamily(activeOrganization ?? ''))

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const myTwin = useRecoilValue(myTwinAtom)

  // check to see if we have permissions
  useFetchCheckPermissions(Feature.email_responder)

  const { twinResponder, setTwinResponder } = useFetchTwinResponder(myTwin?.id ?? '')

  // advanced settings
  const {
    isAutoSendEnabled,
    setIsAutoSendEnabled,
    labelForSalesEmails,
    setLabelForSalesEmails,
    moveSalesEmailsToLabel,
    setMoveSalesEmailsToLabel,
    labelForNoReplyEmails,
    setLabelForNoReplyEmails,
    moveNoReplyEmailsToLabel,
    setMoveNoReplyEmailsToLabel,
    labelForDraftEmails,
    setLabelForDraftEmails,
  } = useAdvancedSettings()

  useEffect(() => {
    // when twin responder is fetched, we set the advanced settings
    if (twinResponder) {
      setMoveSalesEmailsToLabel(!twinResponder.sales_label_keep_inbox ?? false)
      setMoveNoReplyEmailsToLabel(!twinResponder.noreply_label_keep_inbox ?? false)
      setLabelForDraftEmails(twinResponder.draft_label ?? '')
      setLabelForSalesEmails(twinResponder.sales_label ?? '')
      setLabelForNoReplyEmails(twinResponder.noreply_label ?? '')
      setIsReplyAllEnabled(twinResponder.reply_all_enabled ?? false)
      setIsAutoSendEnabled(twinResponder.auto_send_enabled ?? false)
    }
  }, [
    twinResponder,
    setMoveSalesEmailsToLabel,
    setMoveNoReplyEmailsToLabel,
    setLabelForDraftEmails,
    setLabelForSalesEmails,
    setLabelForNoReplyEmails,
    setIsAutoSendEnabled,
  ])

  // permissions state
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false)
  const onClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false)
    // we force the toggle to be off when the user closes permission modal
    setResponderStatus(TwinResponderUpdateStatus.inactive)
  }
  const gmailPermissions = useRecoilValue(featurePermissionsAtomFamily(Feature.email_responder))
  const hasPermissions = gmailPermissions?.isPermitted ?? false
  const acceptedPermissions = gmailPermissions?.acceptedPermissions ?? []
  const missingPermissions = gmailPermissions?.missingPermissions ?? []

  const [responderStatus, setResponderStatus] = useState<TwinResponderUpdateStatus>(
    twinResponder ? twinResponder.status : TwinResponderUpdateStatus.inactive
  )
  const onTwinResponderStatusChange = async (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    // we only set changes if we have permissions
    if (hasPermissions) {
      setResponderStatus(checked ? TwinResponderUpdateStatus.active : TwinResponderUpdateStatus.inactive)
      setHasChanges(true)
    }
    if (checked && !hasPermissions) {
      setIsPermissionsModalOpen(true)
    }
  }
  useEffect(() => {
    if (searchParams.get('isActivateEmailResponderActive') === 'true') {
      setResponderStatus(TwinResponderUpdateStatus.active)
    } else if (twinResponder) {
      setResponderStatus(twinResponder.status)
    }
  }, [twinResponder, searchParams])

  // state for whether or not reply all is enabled
  const [isReplyAllEnabled, setIsReplyAllEnabled] = useState(false)
  const onReplyAllChange = async (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsReplyAllEnabled(event.target.checked)
    setHasChanges(true)
  }

  const onUpdateSettings = async () => {
    setIsSubmitting(true)
    setSubmitCount(submitCount + 1)

    if (!activeOrganization || !myTwin) {
      return null
    }

    try {
      // update twin responder status
      await usersUpdateTwinResponder(activeOrganization, myTwin.id, {
        status: responderStatus,
        noreply_label: labelForNoReplyEmails,
        noreply_label_keep_inbox: labelForNoReplyEmails === '' ? undefined : !moveNoReplyEmailsToLabel,
        sales_label: labelForSalesEmails,
        sales_label_keep_inbox: labelForSalesEmails === '' ? undefined : !moveSalesEmailsToLabel,
        draft_label: labelForDraftEmails,
        reply_all_enabled: isReplyAllEnabled,
        auto_send_enabled: isAutoSendEnabled,
      })
      // hydrate recoil with updated twin responder status
      setTwinResponder((prev) => {
        if (prev) {
          return {
            ...prev,
            status: responderStatus,
            noreply_label: labelForNoReplyEmails || undefined, // if label is empty, set as undefined
            noreply_label_keep_inbox: labelForNoReplyEmails === '' ? undefined : !moveNoReplyEmailsToLabel,
            sales_label: labelForSalesEmails || undefined,
            sales_label_keep_inbox: labelForSalesEmails === '' ? undefined : !moveSalesEmailsToLabel,
            draft_label: labelForDraftEmails || undefined,
            reply_all_enabled: isReplyAllEnabled,
            auto_send_enabled: isAutoSendEnabled,
          }
        }
        return null
      })

      // update onboarding step if it has not yet been set
      const isEmailResponderCompleted = user?.onboarding_steps_completed.includes(OnboardingStep.email_responder)
      if (!isEmailResponderCompleted) {
        updateOnboardingStep()
      }

      setToast({ title: 'Updated email responder settings', severity: 'success' })
    } catch (error) {
      setToast({ title: 'Failed to update email responder settings', severity: 'error' })
    } finally {
      setIsSubmitting(false)
      setHasChanges(false)
    }
  }

  // local state for checking if there are changes
  const [hasChanges, setHasChanges] = useState<boolean>(false)

  // we need to make an additional feature permissions call specifically for advanced settings
  const { featurePermissions: advancedSettingsFeaturePermissions, isLoading: isLoadingPermissions } =
    useFetchCheckPermissions(Feature.advanced_email_responder)
  const advancedSettingsMissingPermissions = useMemo(() => {
    return advancedSettingsFeaturePermissions?.missingPermissions ?? []
  }, [advancedSettingsFeaturePermissions])
  const showAdvancedSettingsBanner =
    !user?.onboarding_steps_completed.includes(OnboardingStep.advanced_email_responder_banner_shown) &&
    advancedSettingsMissingPermissions.length > 0

  if (isLoadingPermissions) {
    return (
      <PageContainerStack>
        <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
      </PageContainerStack>
    )
  }

  const isJelledEmailDomain = JELLED_EMAIL_DOMAINS.includes(organization?.email_domain ?? '')

  return (
    <PageContainerStack>
      {!isMobile && (
        <Typography variant="h4" mb={3}>
          Emails
        </Typography>
      )}
      {showAdvancedSettingsBanner && (
        <Stack mb={2}>
          <AdvancedSettingsBannerWithOnboardingStep
            acceptedPermissions={advancedSettingsFeaturePermissions?.acceptedPermissions ?? []}
            missingPermissions={advancedSettingsFeaturePermissions?.missingPermissions ?? []}
          />
        </Stack>
      )}

      <Typography
        mb={5}
        variant="pS"
        sx={{
          color: COLORS.GRAY_2,
        }}>
        Get summarized insights and automatically drafted responses.
      </Typography>
      <Stack maxWidth="1024px">
        <Typography variant="h5" mb={1}>
          Drafts
        </Typography>
        <Typography
          variant="pXS"
          sx={{
            color: COLORS.GRAY_3,
          }}>
          Get intelligently drafted emails that incorporate your twin’s knowledge base.
        </Typography>

        <JelledSwitch
          disabled={isSubmitting}
          label={`Activate automatic draft generation`}
          checked={responderStatus === TwinResponderUpdateStatus.active && hasPermissions}
          onChange={onTwinResponderStatusChange}
          style={{
            color: COLORS.GRAY_0,
            marginTop: '24px',
            width: isMobile ? '326px' : '500px',
          }}
        />

        <Stack maxWidth="1024px" mb={3} mt={3}>
          <Stack direction="row" alignItems="center">
            <JelledSwitch
              disabled={isSubmitting}
              label={'Reply all'}
              checked={isReplyAllEnabled}
              onChange={onReplyAllChange}
              style={{
                color: COLORS.GRAY_0,
                width: '500px',
                marginBottom: '10px',
              }}
            />
          </Stack>
          <Typography
            variant="pXS"
            sx={{
              color: COLORS.GRAY_2,
              maxWidth: '457px',
            }}>
            Address drafts to the sender and anyone in the “To:” and “Cc:” lines. This will not automatically send the
            draft to all recipients.
          </Typography>
        </Stack>

        {isJelledEmailDomain && (
          <JelledSwitch
            disabled={isSubmitting}
            label={`Send drafts automatically`}
            checked={isAutoSendEnabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              setIsAutoSendEnabled(checked)
            }}
            style={{
              color: COLORS.GRAY_0,
              width: '500px',
              marginBottom: '24px',
            }}
          />
        )}

        <Divider
          sx={{
            marginBottom: '24px',
          }}
        />

        <Stack mb={12}>
          <AdvancedSettings
            moveSalesEmailsToLabel={moveSalesEmailsToLabel}
            setMoveSalesEmailsToLabel={setMoveSalesEmailsToLabel}
            moveNoReplyEmailsToLabel={moveNoReplyEmailsToLabel}
            setMoveNoReplyEmailsToLabel={setMoveNoReplyEmailsToLabel}
            labelForDraftEmails={labelForDraftEmails}
            setLabelForDraftEmails={setLabelForDraftEmails}
            labelForSalesEmails={labelForSalesEmails}
            setLabelForSalesEmails={setLabelForSalesEmails}
            labelForNoReplyEmails={labelForNoReplyEmails}
            setLabelForNoReplyEmails={setLabelForNoReplyEmails}
            advancedSettingsFeaturePermissions={advancedSettingsFeaturePermissions}
            allowVisibilityToggle={false} // we don't want to show the toggle behavior since we are in settings already
            setHasChanges={setHasChanges}
          />
        </Stack>

        {isPermissionsModalOpen && (
          <SetupPermissionsModal
            additionalParams={{
              isSettingsModalOpen: 'true', // used to open the settings modal after permissions are granted
              isActivateEmailResponderActive: 'true', // used to turn the toggle on after permissions are granted
            }}
            onClose={onClosePermissionsModal}
            acceptedPermissions={acceptedPermissions}
            missingPermissions={missingPermissions}
            feature={Feature.email_responder}
          />
        )}
        {hasChanges && (
          <FloatingButtonBar
            onCancelClicked={() => {
              setHasChanges(false)
              // revert all the inputs back to the original values
              if (twinResponder) {
                setResponderStatus(twinResponder.status)
                setIsReplyAllEnabled(twinResponder.reply_all_enabled ?? false)
                setMoveSalesEmailsToLabel(twinResponder.sales_label_keep_inbox ?? false)
                setMoveNoReplyEmailsToLabel(twinResponder.noreply_label_keep_inbox ?? false)
                setLabelForDraftEmails(twinResponder.draft_label ?? '')
                setLabelForSalesEmails(twinResponder.sales_label ?? '')
                setLabelForNoReplyEmails(twinResponder.noreply_label ?? '')
                setIsAutoSendEnabled(twinResponder.auto_send_enabled ?? false)
              }
            }}
            updateLabel="Save"
            onUpdateClicked={onUpdateSettings}
            isUpdating={isSubmitting}
          />
        )}
      </Stack>
    </PageContainerStack>
  )
}

export default withOnboardingStep(EmailSettings, OnboardingStep.email_responder)
